import { useState, useCallback } from 'preact/hooks';
import type { ContactInfo, I18nStrings } from '@bitio-builder/shared';

interface ContactFormProps {
  onSubmit: (contactInfo: ContactInfo) => void;
  isSubmitting: boolean;
  strings: I18nStrings;
}

const containerStyle: Record<string, string | number> = {
  padding: '24px',
  background: 'white',
};

const headerStyle: Record<string, string | number> = {
  marginBottom: '24px',
};

const titleStyle: Record<string, string | number> = {
  fontSize: '16px',
  fontWeight: 600,
  color: 'var(--bb-foreground, #1A1C17)',
  marginBottom: '4px',
};

const subtitleStyle: Record<string, string | number> = {
  fontSize: '12px',
  color: 'var(--bb-muted, #6B7280)',
};

const formStyle: Record<string, string | number> = {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
};

const fieldStyle: Record<string, string | number> = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
};

const labelStyle: Record<string, string | number> = {
  fontSize: '12px',
  fontWeight: 500,
  color: 'var(--bb-foreground, #1A1C17)',
};

const inputStyle: Record<string, string | number> = {
  padding: '8px 16px',
  border: '1px solid var(--bb-border, #E5E7EB)',
  borderRadius: '8px',
  fontSize: '14px',
  outline: 'none',
  fontFamily: 'inherit',
};

const inputErrorStyle: Record<string, string | number> = {
  ...inputStyle,
  borderColor: 'var(--bb-error, #DC2626)',
};

const errorMessageStyle: Record<string, string | number> = {
  fontSize: '12px',
  color: 'var(--bb-error, #DC2626)',
};

const submitButtonStyle: Record<string, string | number> = {
  width: '100%',
  padding: '16px',
  background: 'var(--bb-primary, #4D5D32)',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  fontSize: '14px',
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

interface FormData {
  name: string;
  email: string;
  phone: string;
  company: string;
  role: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  company?: string;
}

export function ContactForm({ onSubmit, isSubmitting, strings }: ContactFormProps) {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    company: '',
    role: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});

  const validate = useCallback((): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Namn krävs';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'E-post krävs';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Ogiltig e-postadress';
    }

    if (!formData.company.trim()) {
      newErrors.company = 'Företag krävs';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleSubmit = useCallback(
    (e: Event) => {
      e.preventDefault();

      if (!validate()) return;

      const contactInfo: ContactInfo = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        company: formData.company.trim(),
        phone: formData.phone.trim() || undefined,
        role: formData.role.trim() || undefined,
      };

      onSubmit(contactInfo);
    },
    [formData, validate, onSubmit]
  );

  const handleChange = useCallback((field: keyof FormData) => {
    return (e: Event) => {
      const value = (e.target as HTMLInputElement).value;
      setFormData((prev) => ({ ...prev, [field]: value }));
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    };
  }, []);

  const { contactForm: s } = strings;

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <h3 style={titleStyle}>{s.title}</h3>
        <p style={subtitleStyle}>{s.subtitle}</p>
      </div>

      <form style={formStyle} onSubmit={handleSubmit}>
        <div style={fieldStyle}>
          <label style={labelStyle}>{s.name} *</label>
          <input
            type="text"
            value={formData.name}
            onInput={handleChange('name')}
            style={errors.name ? inputErrorStyle : inputStyle}
            disabled={isSubmitting}
          />
          {errors.name && <span style={errorMessageStyle}>{errors.name}</span>}
        </div>

        <div style={fieldStyle}>
          <label style={labelStyle}>{s.email} *</label>
          <input
            type="email"
            value={formData.email}
            onInput={handleChange('email')}
            style={errors.email ? inputErrorStyle : inputStyle}
            disabled={isSubmitting}
          />
          {errors.email && <span style={errorMessageStyle}>{errors.email}</span>}
        </div>

        <div style={fieldStyle}>
          <label style={labelStyle}>{s.phone}</label>
          <input
            type="tel"
            value={formData.phone}
            onInput={handleChange('phone')}
            style={inputStyle}
            disabled={isSubmitting}
          />
        </div>

        <div style={fieldStyle}>
          <label style={labelStyle}>{s.company} *</label>
          <input
            type="text"
            value={formData.company}
            onInput={handleChange('company')}
            style={errors.company ? inputErrorStyle : inputStyle}
            disabled={isSubmitting}
          />
          {errors.company && <span style={errorMessageStyle}>{errors.company}</span>}
        </div>

        <div style={fieldStyle}>
          <label style={labelStyle}>{s.role}</label>
          <input
            type="text"
            value={formData.role}
            onInput={handleChange('role')}
            style={inputStyle}
            disabled={isSubmitting}
          />
        </div>

        <button
          type="submit"
          style={{ ...submitButtonStyle, opacity: isSubmitting ? 0.7 : 1 }}
          disabled={isSubmitting}
        >
          {isSubmitting ? s.submitting : s.submit}
        </button>
      </form>
    </div>
  );
}
